import React from 'react';
import { Property } from '../../../types';
import { XMarkIcon } from '../../Icons';
import { isNegociacionEspecial } from './visitNegotiationUtils';

interface VisitPurposeSelectorProps {
  property: Property;
  onSelect: (type: 'reconocimiento' | 'cliente') => void;
  onClose: () => void;
}

export const VisitPurposeSelector: React.FC<VisitPurposeSelectorProps> = ({
  property,
  onSelect,
  onClose,
}) => {
  const isSpecialNegotiation = isNegociacionEspecial(property);

  return (
    <div className="flex-grow flex flex-col justify-start sm:justify-center p-6 sm:p-8 bg-gray-50/50 relative overflow-y-auto">
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 sm:top-6 sm:right-6 text-gray-400 hover:text-red-500 transition-colors z-10"
      >
        <XMarkIcon className="w-6 h-6" />
      </button>

      <div className="max-w-xl mx-auto w-full text-center space-y-4 sm:space-y-6 animate-fade-in-up my-auto py-2">
        <div className="space-y-1">
          <span className="inline-block px-3 py-1 bg-sky-50 text-sky-700 text-[9px] font-black rounded-full uppercase tracking-widest mb-1">
            Configuración de Visita
          </span>
          <h4 className="text-lg sm:text-xl font-black text-[#00234F] tracking-tight uppercase">
            ¿Cuál es el propósito de tu visita?
          </h4>
          <p className="text-xs text-gray-500 font-medium leading-normal">
            Elige una opción para continuar con el agendamiento del inmueble <strong>{property.codigo}</strong>
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {/* Opción Reconocimiento (Bloqueada si es Negociación Especial) */}
          <button
            type="button"
            disabled={isSpecialNegotiation}
            onClick={() => !isSpecialNegotiation && onSelect('reconocimiento')}
            className={`flex sm:flex-col items-center sm:justify-between p-3.5 sm:p-5 border rounded-2xl transition-all text-left sm:text-center shadow-sm sm:min-h-[220px] gap-3 sm:gap-0 ${
              isSpecialNegotiation
                ? 'bg-gray-100/80 border-gray-200 opacity-70 cursor-not-allowed'
                : 'bg-white border-gray-200 hover:border-sky-500 hover:ring-2 hover:ring-sky-100 group active:scale-[0.98]'
            }`}
          >
            <div className="flex flex-row sm:flex-col items-center gap-3 sm:gap-0 w-full">
              <div
                className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isSpecialNegotiation ? 'bg-gray-200 text-gray-400' : 'bg-sky-50 text-sky-600 group-hover:bg-sky-100 transition-colors'
                }`}
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <div className="flex flex-col sm:items-center">
                <span className={`font-black text-xs sm:text-sm sm:mt-3.5 uppercase tracking-wider ${isSpecialNegotiation ? 'text-gray-400' : 'text-[#00234F] group-hover:text-sky-700'}`}>
                  Reconocimiento
                </span>
                <span className="text-[10px] sm:text-[11px] text-gray-400 mt-0.5 sm:mt-1.5 max-w-[200px] leading-relaxed">
                  Conoce el inmueble individualmente. <strong>No requiere</strong> datos de cliente.
                </span>
              </div>
            </div>

            {isSpecialNegotiation ? (
              <span className="mt-3 inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-800 uppercase tracking-wide">
                No disponible en Negociación Especial
              </span>
            ) : (
              <span className="hidden sm:inline-block mt-4 text-[10px] font-bold text-sky-600 uppercase tracking-wider group-hover:underline">
                Seleccionar &rarr;
              </span>
            )}
          </button>

          {/* Opción Visita con Cliente */}
          <button
            type="button"
            onClick={() => onSelect('cliente')}
            className="flex sm:flex-col items-center sm:justify-between p-3.5 sm:p-5 bg-white border border-gray-200 rounded-2xl hover:border-emerald-500 hover:ring-2 hover:ring-emerald-100 transition-all text-left sm:text-center shadow-sm group active:scale-[0.98] sm:min-h-[220px] gap-3 sm:gap-0"
          >
            <div className="flex flex-row sm:flex-col items-center gap-3 sm:gap-0 w-full">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:bg-emerald-100 transition-colors flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="flex flex-col sm:items-center">
                <span className="font-black text-xs sm:text-sm text-[#00234F] sm:mt-3.5 uppercase tracking-wider group-hover:text-emerald-700">
                  Visita con Cliente
                </span>
                <span className="text-[10px] sm:text-[11px] text-gray-400 mt-0.5 sm:mt-1.5 max-w-[200px] leading-relaxed">
                  Acompaña a tu cliente potencial. <strong>Requiere</strong> registrar sus datos.
                </span>
              </div>
            </div>
            <span className="hidden sm:inline-block mt-4 text-[10px] font-bold text-emerald-600 uppercase tracking-wider group-hover:underline">
              Seleccionar &rarr;
            </span>
          </button>
        </div>

        <div className="pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-1.5 border border-gray-300 hover:bg-gray-100 text-gray-500 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};
